import React from "react";
import { motion } from "framer-motion";
import { FileText, Lock, Truck } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: FileText,
    title: "Post your job",
    desc: "House removal, waste clearance, or office move — drop the details and get matched quotes from verified Luton van drivers in minutes.",
    tag: "For customers",
  },
  {
    n: "02",
    icon: Lock,
    title: "Accept &amp; escrow",
    desc: "Funds are charged and held in Stripe escrow the moment you accept a quote. No upfront chase. No fraud risk.",
    tag: "Auto-secured",
  },
  {
    n: "03",
    icon: Truck,
    title: "Complete &amp; release",
    desc: "Driver uploads proof of completion via WhatsApp. Funds auto-capture, commission deducted, payout queued for Friday.",
    tag: "For drivers",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-it-works-section"
      className="bg-[#F8FAFC] border-y border-[#0A0A0A]/5"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              How it works
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
              Three steps. Money flows
              <br />
              when the job&apos;s done.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#475569] leading-relaxed">
              Raanah replaces phone calls, advances, and disputes with a single
              auditable flow. Every quote, capture, and release is logged — so
              drivers focus on the job, not chasing the cash.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0A0A0A]/10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
                data-testid={`how-step-${i + 1}`}
                className="group relative bg-white border-r border-b border-[#0A0A0A]/10 p-8 md:p-10 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span className="font-display text-5xl font-black tracking-tighter text-[#0A0A0A] group-hover:text-white">
                    {s.n}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold border border-[#0A0A0A]/15 group-hover:border-white/30 px-2 py-1 text-[#475569] group-hover:text-white/80">
                    {s.tag}
                  </span>
                </div>
                <div className="mt-12">
                  <Icon
                    size={22}
                    className="text-[#1E4D55] group-hover:text-[#5BB247] transition-colors"
                  />
                  <h3
                    className="font-display mt-4 text-2xl font-bold tracking-tight text-[#0A0A0A] group-hover:text-white"
                    dangerouslySetInnerHTML={{ __html: s.title }}
                  />
                  <p
                    className="mt-3 text-[#475569] group-hover:text-white/75 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: s.desc }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
